// @flow

import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';

import Slider from '../../components/Slider';
import SliderElement from '../../components/SliderElement';
import {
  MALL_EVENTS,
  MALL_FLOW,
  MALL_INFO,
  MALL_MAP,
  MALL_MEDIA,
  MALL_PERF,
  MALL_SHOPPERS,
} from '../../path/mallPath';
import MallMap from '../mall/map/MallMap';
import MallData from '../mall/data/MallData';
import MallMedia from '../mall/media/MallMedia';
import {
  getCurrentOfficePcId,
  getCurrentPcId,
  getShoppingCenterNameByPcId,
  parseUrl,
} from '../../utils/urlUtils';
import OfficeData from '../office/data/OfficeData';
import OfficeMedia from '../office/media/OfficeMedia';
import { OFFICE_INFO, OFFICE_MEDIA } from '../../path/officePath';
import { embeddedUrlPage, isMobile } from '../../const';
import {
  fetchBranchLevel,
  fetchGeminiRights,
  fetchShoppingCenterMedia,
} from '../../state/shopping-centers/shoppingCentersService';
import officeApi from '../../network/api/officeApi';
import type { Office } from '../../types/OfficesType';
import BuildingSelector from '../office/BuildingSelector';
import MallMarketing from '../mall/marketing/MallMarketing';
import type { GeminiRights, ShoppingCenter } from '../../types/shoppingCenterType';
import type { MediaGallery } from '../../types/keepeekType';
import MallFlow from '../mall/flow/MallFlow';
import MallEvents from '../mall/events/MallEvents';
import marketingApi from '../../network/api/marketingApi';
import type { KeyFigureType } from '../../types/marketingType';
import { HEADER_EVENTS, HEADER_MAP, HEADER_SHOPPERS } from '../../path/headerPaths';
import shoppingCenterApi from '../../network/api/shoppingCenterApi';
import eventsApi from '../../network/api/eventsApi';
import { WORLD_MAP } from '../../path/worldPaths';
import { fetchOfficesMedias } from '../../state/offices/officesService';
import { addToDisabledNoDataTabs, resetDisabledNoDataTabs } from '../../state/page/pageService';
import { notifyApolloError, notifyInfoMallDisabledHeader } from '../../network/notification';
import type { EmbeddedUrl } from '../../types/embeddedUrlTypes';
import { isAssetInDevelopment } from '../../utils/utils';

type Props = {
  // Redux
  dispatch: () => void,
  history: { push: Function },
  location: Location,
  baseShoppingCenterList: ShoppingCenter[],
  shoppingCenterMedia: MediaGallery[],
  isMediaLoading: boolean,
  userGeminiRights: GeminiRights
}

type State = {
  currentSlideIndex: number,
  office: Office,
  isKeyFiguresLoading: boolean,
  keyFigures: KeyFigureType,
  embeddedUrlsMedia: EmbeddedUrl[],
  isEmbeddedLoading: boolean,
}

class MallBase extends React.Component<Props, State> {
  state: State = {
    currentSlideIndex: 1,
    office: {},
    isKeyFiguresLoading: true,
    keyFigures: null,
    isEmbeddedLoading: true,
  };

  params = parseUrl(this.props.location.search);

  fetchShoppingCenterData(pcId) {
    this.props.dispatch(fetchGeminiRights(pcId));
    this.props.dispatch(fetchBranchLevel(pcId));
    this.props.dispatch(fetchShoppingCenterMedia(pcId));
    marketingApi.getKeyFigures(pcId)
      .then(res => res.json())
      .then((keyFigures: KeyFigureType) => this.setState({ keyFigures }))
      .catch(() => this.setState({ keyFigures: null }))
      .finally(() => {
        this.setState({
          isKeyFiguresLoading: false,
        });
      });

    // disable tabs if necessary
    const isPerf = this.props.location.pathname.includes(MALL_PERF);
    shoppingCenterApi.hasMapData(pcId)
      .catch(() => {
        this.disableNoDataTabs(pcId, HEADER_MAP, I18n.t(`header.${isPerf ? 'PERF' : 'MAP'}`));
      });
    eventsApi.hasEventData(pcId)
      .catch(() => {
        this.disableNoDataTabs(pcId, HEADER_EVENTS, I18n.t('header.EVENTS'));
      });

    const sc = getShoppingCenterNameByPcId(pcId);
    if (isAssetInDevelopment(sc)) {
      this.disableNoDataTabs(pcId, HEADER_SHOPPERS, I18n.t('header.SHOPPERS'));
    }
    this.fetchEmbeddedUrlMedia(pcId);
  }

  fetchEmbeddedUrlMedia = (pcId: string) => {
    shoppingCenterApi.fetchEmbeddedUrls(pcId, embeddedUrlPage.media)
      .then(res => res.json())
      .then((embeddedUrlsMedia: EmbeddedUrl[]) => {
        this.setState({ embeddedUrlsMedia });
      })
      .catch(() => this.setState({ embeddedUrlsMedia: null }))
      .finally(() => {
        this.setState({ isEmbeddedLoading: false });
      });
  }

  componentDidMount() {
    if (this.params.mall) {
      const pcId = getCurrentPcId();
      shoppingCenterApi.checkShoppingCenterData(pcId)
        .then(() => this.fetchShoppingCenterData(pcId))
        .catch((e) => {
          this.props.history.push(WORLD_MAP);
          this.props.dispatch(notifyApolloError(e));
        });
    } else if (this.params.office) {
      const id = getCurrentOfficePcId();
      officeApi.fetchOffice(id)
        .then(response => response.json())
        .then((responseJson) => {
          this.setState({ office: responseJson });
          this.props.dispatch(fetchOfficesMedias(id));
        }).catch((e) => {
          this.props.history.push(WORLD_MAP);
          this.props.dispatch(notifyApolloError(e));
        });
    }
  }

  componentWillUnmount() {
    this.props.dispatch(resetDisabledNoDataTabs());
  }

  disableNoDataTabs = (scId: number, path: string, page: string) => {
    this.props.dispatch(addToDisabledNoDataTabs(path));
    if (this.props.location.pathname.includes(path)) {
      this.props.dispatch(notifyInfoMallDisabledHeader(page));
      this.props.history.push(`${MALL_INFO}?mall=${scId}`);
    }
  };

  render() {
    let sliderContent;
    if (this.params.mall) {
      const isPerf = this.props.location.pathname.includes(MALL_PERF);
      sliderContent = (
        <Slider
          goTopOnUpdate
          updateCurrentSlide={currentSlideIndex => this.setState({ currentSlideIndex })}
          selectedIndex={this.state.currentSlideIndex}
        >
          <SliderElement
            isLazy={isMobile}
            path={MALL_INFO}
            render={
              <MallData
                location={this.props.location}
                shoppingCenters={this.props.baseShoppingCenterList}
                isKeyFiguresLoading={this.state.isKeyFiguresLoading}
                keyFigures={this.state.keyFigures}
              />
            }
          />
          <SliderElement
            isLazy
            path={MALL_MAP}
            render={
              <MallMap
                isPerf={isPerf}
                dispatch={this.props.dispatch}
                userGeminiRights={this.props.userGeminiRights}
              />
            }
          />
          <SliderElement isLazy path={MALL_FLOW} render={<MallFlow />} />
          {/*<SliderElement*/}
          {/*  isLazy*/}
          {/*  path={MALL_SHOPPERS}*/}
          {/*  render={*/}
          {/*    <MallMarketing*/}
          {/*      dispatch={this.props.dispatch}*/}
          {/*      shoppingCenters={this.props.baseShoppingCenterList}*/}
          {/*      shoppingCenterMedia={this.props.shoppingCenterMedia}*/}
          {/*      isKeyFiguresLoading={this.state.isKeyFiguresLoading}*/}
          {/*      keyFigures={this.state.keyFigures}*/}
          {/*    />*/}
          {/*  }*/}
          {/*/>*/}
          <SliderElement
            isLazy={isMobile}
            path={MALL_MEDIA}
            render={
              <MallMedia
                embeddedUrls={this.state.embeddedUrlsMedia}
                isEmbeddedLoading={this.state.isEmbeddedLoading}
                shoppingCenterMedia={this.props.shoppingCenterMedia}
                isMediaLoading={this.props.isMediaLoading}
              />
          }
          />
          <SliderElement
            isLazy
            path={MALL_EVENTS}
            render={<MallEvents />}
          />
        </Slider>
      );
    } else if (this.params.office) {
      sliderContent = (
        <BuildingSelector office={this.state.office}>
          <Slider
            goTopOnUpdate
            updateCurrentSlide={currentSlideIndex => this.setState({ currentSlideIndex })}
            selectedIndex={this.state.currentSlideIndex}
          >
            <SliderElement
              path={OFFICE_INFO}
              render={<OfficeData office={this.state.office} />}
            />
            <SliderElement
              path={OFFICE_MEDIA}
              render={<OfficeMedia office={this.state.office} />}
            />
          </Slider>
        </BuildingSelector>
      );
    }
    return (
      <div className="slider-container">
        {sliderContent}
      </div>
    );
  }
}

export default withRouter(connect(state => ({
  baseShoppingCenterList: state.shoppingCenterFilters.baseShoppingCenterList,
  shoppingCenterMedia: state.shoppingCenterFilters.shoppingCenterMedia,
  userGeminiRights: state.shoppingCenterFilters.userGeminiRights,
  isMediaLoading: state.page.isMediaLoading,
}))(MallBase));

